import { useTransition } from "react";
import type {
  Process,
  RollupWithTagsAndCountsAndRange,
} from "../../types/types";
import {
  clearAllTrendChartBars,
  clearAllAggregates,
} from "../../providers/GlobalStateProvider";
import { askrClients } from "../../providers/AskrProvider";
import {
  FETCH_POLICY,
  isLargeQueryError,
  queryAndUpdateAggregateProps,
} from "./aggregatesGetterUtils";
import { useFocusRange } from "utils/customHooks/genericHooks";
import { useDispatch } from "react-redux";
import { AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import useQueryWithErrorHandling from "utils/useQuery";
import { mapAggregateToRollup } from "utils/metrics";

type AggregateForOneOrManyRangesGetterProps = {
  process: Process;
  variables: { ge: string; lt: string; machemetrySourceName: string };
  dispatchFn: (rollups: RollupWithTagsAndCountsAndRange[]) => void;
  query: any;
  queryTitle: string;
  pollInterval: number;
  shouldQuery: boolean;
};
export const AggregateGetterSingle = ({
  process,
  variables,
  query,
  dispatchFn,
  queryTitle,
  pollInterval,
  shouldQuery,
}: AggregateForOneOrManyRangesGetterProps) => {
  const dispatch = useDispatch();
  const focusRange = useFocusRange();

  const { ckey } = process;
  const client = askrClients.get(ckey);

  const range = {
    ge: variables.ge,
    lt: variables.lt,
  };
  const isInvertedTimeRange = Number(range.lt) < Number(range.ge);

  const skip = !focusRange || !shouldQuery || isInvertedTimeRange;

  return skip ? null : (
    <QuerySingle
      {...{
        query,
        variables,
        range,
        dispatchFn,
        pollInterval,
        dispatch,
        queryTitle,
        client,
      }}
    />
  );
};

function QuerySingle({
  query,
  variables,
  range,
  dispatchFn,
  pollInterval,
  client,
  dispatch,
  queryTitle,
}: any) {
  const [, startTransition] = useTransition();

  useQueryAndUpdateSingleAggregate({
    query,
    variables: { ...variables, ...range },
    dispatchFn,
    pollInterval,
    onError: err => {
      // if we get an error back for trend chart bars, clear the bars
      if (isLargeQueryError(err)) {
        startTransition(() => {
          dispatch(
            queryTitle === AGGREGATES_FOR_TREND_CHART_BARS_QUERY_TITLE
              ? clearAllTrendChartBars()
              : clearAllAggregates()
          );
        });
        // if we get one error, stop querying
        // setShouldQuery(false);
      }
    },
    client,
    queryTitle,
  });
  return null;
}

function useQueryAndUpdateSingleAggregate({
  query,
  variables,
  client,
  dispatchFn,
  pollInterval = null,
  onError,
  queryTitle,
}: queryAndUpdateAggregateProps) {
  const { error, loading } = useQueryWithErrorHandling(
    queryTitle,
    query,

    {
      // run it if the trends page is not filtered to "all" shifts
      ...(pollInterval ? { pollInterval } : {}),
      client,
      variables,
      fetchPolicy: FETCH_POLICY,
      onError: (...args) => {
        return onError && onError(...args);
      },
      onCompleted: data => {
        if (data && !loading && !error) {
          const { productionAggregates } = data;
          if (!productionAggregates) {
            return;
          }

          const newRollupsArray =
            productionAggregates.map(mapAggregateToRollup);

          // for single-aggregate queries,
          // we get an array with one item

          dispatchFn(newRollupsArray);
        }
      },
    }
  );
}
